<template>
  <top-nav />
  <div class="max-w-3xl px-4 mx-auto sm:px-6 lg:px-8">
    <div class="py-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Review Tasks</h1>
      <div class="text-gray-500">Review tasks that have been created by OpenAI's GPT-3.</div>
    </div>

    <!-- Empty State (no tasks) -->
    <div v-if="tasksLoaded && !reviewTasks.length" class="mt-10 text-gray-600 dark:text-gray-300">No tasks to review.</div>

    <div v-else class="space-y-10">
      <template v-for="(task, index) in reviewTasks" :key="index">
        <div class="overflow-hidden bg-white border-none rounded-md dark:bg-gray-900">
          <form @submit="approveTask($event, index)">
            <div class="p-4 space-y-5">
              <!-- Header -->
              <div class="pb-3 space-y-2 border-b border-gray-500">
                <div class="flex items-start justify-between">
                  <div>
                    <div class="flex items-center space-x-2">
                      <img :src="task.source.publisher.icon" class="w-4 h-4 rounded" />
                      <div class="text-sm text-gray-500">{{ task.source.publisher.name || task.source.publisher.url }}, saved by {{ task.user }}</div>
                    </div>
                    <div class="flex items-center text-sm">
                      <p class="text-gray-900 line-clamp-1 dark:text-gray-100">
                        {{ task.source.title }}
                      </p>
                      <div class="ml-1">
                        <a :href="task.source.url" class="text-blue-500">
                          <ExternalLinkIcon class="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div v-if="Number.isInteger(task.gpt?.contentRating)" class="text-sm text-right text-gray-500">
                    Content rating:
                    <span
                      :class="{
                        'text-red-500': task.gpt.contentRating >= 2,
                        'text-yellow-500': task.gpt.contentRating == 1,
                        'text-green-500': task.gpt.contentRating == 0,
                      }"
                    >
                      {{ task.gpt.contentRating }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Task Type -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Task Type </label>
                <div class="mt-1">
                  <select
                    name="type"
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                    v-model="task.type"
                  >
                    <option :selected="task.type == 'multipleChoice'" value="multipleChoice">Multiple Choice</option>
                    <option :selected="task.type == 'multipleAnswer'" value="multipleAnswer">Multiple Answer</option>
                    <option :selected="task.type == 'trueFalse'" value="trueFalse">True/False</option>
                    <option :selected="task.type == 'textShort'" value="textShort">Text</option>
                    <!-- <option :selected="task.type == 'textLong'" value="textLong">
                            Text Long
                          </option> -->
                    <!-- <option :selected="task.type == 'sequence'" value="sequence">Sequence</option> -->
                    <option :selected="task.type == 'fillTheBlanks'" value="fillTheBlanks">Fill The Blanks</option>
                    <option :selected="task.type == 'reading'" value="reading">Reading</option>
                  </select>
                </div>
              </div>

              <!-- Title -->
              <div v-if="task.type != 'fillTheBlanks' && task.type != 'reading'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Title/Question </label>
                <div class="mt-1 capitalize-first">
                  <input
                    :value="task.title"
                    type="text"
                    name="title"
                    autocomplete="off"
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                  />
                </div>
              </div>

              <!-- multipleChoice, multipleAnswer  -->
              <div v-if="task.type == 'multipleAnswer' || task.type == 'multipleChoice'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  <span v-if="task.type == 'multipleChoice'">Multiple Choice</span>
                  <span v-if="task.type == 'multipleAnswer'">Multiple Answer</span>
                </label>
                <p v-if="task.type == 'multipleAnswer'" class="mt-1 text-sm text-gray-500">Check <span class="underline">all</span> correct answers.</p>
                <div class="block mt-1 space-y-2" v-if="task.options">
                  <template v-for="(optionIndex, index) in answerOptionsLength(task.options, index)" :key="index">
                    <div class="relative">
                      <div v-if="task.type == 'multipleChoice'" class="absolute inset-y-0 left-0 flex items-center">
                        <input
                          type="checkbox"
                          :checked="task.solution.includes(task.options[index]) && task.type == 'multipleChoice'"
                          :value="task.options[index]"
                          name="solution"
                          class="w-4 h-4 ml-2 border-gray-300 rounded text-slate-600 focus:ring-slate-500"
                        />
                      </div>
                      <div v-if="task.type == 'multipleAnswer'" class="absolute inset-y-0 left-0 flex items-center">
                        <input
                          v-if="task.solution"
                          type="checkbox"
                          :checked="task.solution.includes(task.options[index]) && task.type == 'multipleAnswer'"
                          :value="task.options[index]"
                          name="solution"
                          class="w-4 h-4 ml-2 border-gray-300 rounded text-slate-600 focus:ring-slate-500"
                        />
                        <input v-else type="checkbox" :value="task.options[index]" name="solution" class="w-4 h-4 ml-2 border-gray-300 rounded text-slate-600 focus:ring-slate-500" />
                      </div>
                      <div class="capitalize-first">
                        <input
                          type="text"
                          autocomplete="off"
                          name="options"
                          required
                          v-model="task.options[index]"
                          class="block w-full pl-10 border-gray-300 rounded-md focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                        />
                      </div>
                    </div>
                  </template>

                  <div class="flex items-center space-x-2">
                    <button
                      type="button"
                      class="flex items-center justify-center w-full px-3 py-2 text-xs font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm bg-gray hover:bg-gray-50 focus:outline-none dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
                      @click="task.options.length--"
                    >
                      Remove Option
                    </button>
                    <button
                      type="button"
                      class="flex items-center justify-center w-full px-3 py-2 text-xs font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm bg-gray hover:bg-gray-50 focus:outline-none dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
                      @click="task.options.length++"
                    >
                      Add Option
                    </button>
                  </div>
                </div>
              </div>

              <!-- Sequence -->
              <!-- <div v-if="task.type == 'sequence'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Sequence </label>
                <p class="mt-1 text-sm text-gray-500">Enter answers in the <span class="underline">correct order</span></p>
                <div class="block mt-1 space-y-2">
                  <template v-for="optionIndex in task.options.length" :key="optionIndex">
                    <input
                      type="text"
                      v-model="task.options[optionIndex - 1]"
                      name="solution"
                      autocomplete="off"
                      required
                      class="block w-full border-gray-300 rounded-md focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                    />
                  </template>

                  <div class="flex items-center space-x-2">
                    <button
                      type="button"
                      class="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm bg-gray hover:bg-gray-50 focus:outline-none dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
                      @click="task.options.length--"
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      class="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm bg-gray hover:bg-gray-50 focus:outline-none dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
                      @click="task.options.length++"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div> -->

              <!-- fillTheBlanks -->
              <div v-if="task.type == 'fillTheBlanks'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Fill the Blanks </label>
                <div class="block mt-1 space-y-2">
                  <textarea
                    name="options"
                    :value="task.title"
                    rows="3"
                    autocomplete="off"
                    class="block w-full border border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                  />
                </div>

                <div class="mt-1">
                  <input
                    v-if="task.solution"
                    type="text"
                    name="solution"
                    :value="task?.solution[0]"
                    placeholder="Add fill word(s) here, separated by comma"
                    autocomplete="off"
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                  />
                </div>
              </div>

              <!-- trueFalse -->
              <div v-if="task.type == 'trueFalse'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Select the correct answer </label>
                <div class="flex justify-between w-full mt-1 gap-x-2">
                  <label class="w-full">
                    <div class="flex items-center p-4 border border-gray-100 rounded-md">
                      <input
                        name="solution"
                        type="radio"
                        value="false"
                        :checked="task.solution == 'false'"
                        autocomplete="off"
                        class="w-5 h-5 border-gray-300 text-slate-600 focus:ring-slate-500 dark:bg-gray-900"
                      />
                      <div class="block ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">False</div>
                    </div>
                  </label>
                  <label class="w-full">
                    <div class="flex items-center p-4 border border-gray-100 rounded-md">
                      <input
                        name="solution"
                        type="radio"
                        value="true"
                        :checked="task.solution == 'true'"
                        autocomplete="off"
                        class="w-5 h-5 border-gray-300 text-slate-600 focus:ring-slate-500 dark:bg-gray-900"
                      />
                      <div class="block ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">True</div>
                    </div>
                  </label>
                </div>
              </div>

              <!-- textShort -->
              <div v-if="task.type == 'textShort'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Correct Answer </label>
                <div class="mt-1 capitalize-first">
                  <input
                    v-if="task.solution"
                    type="text"
                    :value="task.solution[0]"
                    name="solution"
                    autocomplete="off"
                    required
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                  />
                  <input
                    v-if="!task.solution[0] && task.type == 'textShort'"
                    type="text"
                    value=""
                    name="solution"
                    autocomplete="off"
                    required
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                  />
                </div>
              </div>

              <!-- reading -->
              <div v-if="task.type == 'reading'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Reading </label>
                <div class="block mt-1 space-y-2">
                  <!-- Bullets -->
                  <template v-if="task.reading.bullets">
                    <div v-for="(bullet, index) in task.reading.bullets" :key="bullet">
                      <div class="flex space-x-3 space-y-2 item-center" v-if="bullet">
                        <textarea
                          :value="bullet"
                          name="bullets"
                          autocomplete="off"
                          required
                          class="block w-full border-gray-300 rounded-md h-14 focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                        />
                        <button type="button" @click="delete task.reading.bullets[index]" class="text-xs text-red-500">Delete</button>
                      </div>
                    </div>
                  </template>

                  <!-- Text -->
                  <template v-else>
                    <text-editor
                      :id="'task-reading-html-' + index"
                      class="border border-gray-300 rounded-md dark:bg-gray-900 dark:text-white"
                      :modelValue="task.reading.text"
                      v-model="task.reading.text"
                    />
                  </template>
                </div>
              </div>

              <!-- CTA -->
              <div>
                <div class="flex items-center justify-between gap-x-4">
                  <button
                    type="button"
                    @click="deleteTask(task.source, task)"
                    class="inline-flex items-center justify-center w-full px-4 py-2 text-sm text-base font-medium text-red-700 bg-transparent border border-transparent rounded-md hover:bg-red-200"
                  >
                    Delete
                  </button>
                  <button
                    type="submit"
                    class="inline-flex items-center justify-center w-full px-4 py-2 text-base font-medium border border-transparent rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import firebaseUser from "../composables/userAuth";
import { db } from "../firebase";
// import { getAuth, onAuthStateChanged } from "firebase/auth";
import TextEditor from "../components/TextEditor.vue";
import TopNav from "../components/nav/TopNav.vue";
import { ExternalLinkIcon } from "@heroicons/vue/outline";
import utilFunctions from "../composables/util";
import { collection, query, onSnapshot, doc, orderBy, limit, updateDoc, addDoc, arrayRemove, deleteDoc, Timestamp, arrayUnion, where } from "firebase/firestore";
import { useToast } from "vue-toastification";
import { ref, watchEffect } from "vue";
export default {
  components: {
    TextEditor,
    TopNav,
    ExternalLinkIcon,
  },
  setup() {
    const { user } = firebaseUser();
    const reviewTasks = ref([]);
    const tasksLoaded = ref(false);
    const { shuffleArray, removeLeadingTrailingCommas, createFillString, getUser, dateString } = utilFunctions();
    const toast = useToast();
    const reviewerUserData = ref({});

    watchEffect(async () => {
      if (user.value) {
        reviewerUserData.value = await getUser(user.value.uid);

        if ("roles" in reviewerUserData.value && reviewerUserData.value.roles?.reviewer) {
          const reviewTasksQuery = query(collection(db, "tasks"), where("review.approved", "==", false), orderBy("createdAt", "desc"), limit(100));

          return onSnapshot(reviewTasksQuery, (querySnapshot) => {
            reviewTasks.value = [];
            querySnapshot.forEach((doc) => {
              reviewTasks.value.push({ ...doc.data(), id: doc.id });
            });
          });
        }

        tasksLoaded.value = true;
      }
    });

    // onBeforeMount(async () => {
    //   const querySnapshot = await getDocs(collection(db, "tasks"));
    //   console.log(querySnapshot.size);
    //   // querySnapshot.forEach((doc) => {
    //   //   // doc.data() is never undefined for query doc snapshots
    //   //   console.log(doc.id, " => ", doc.data());
    //   // });
    // });

    function answerOptionsLength(taskOptions, taskIndex) {
      // TODO enable task type to be changed.
      // Currently if you have a fillTheBlank we can't create a multiple choice question.
      let optionsLength;

      if (!taskOptions) {
        optionsLength = reviewTasks.value[taskIndex].options.push("", "", "");
      } else {
        optionsLength = taskOptions.length;
      }

      return optionsLength;
    }

    async function deleteTask(source, task) {
      try {
        // remove task from source
        const sourceRef = doc(db, "sources", source.id);
        await updateDoc(sourceRef, {
          tasks: arrayRemove(task),
        });

        // delete task doc
        await deleteDoc(doc(db, "tasks", task.id));
        toast.success("Task deleted");
      } catch (e) {
        toast.error("Error deleting task");
        console.error("Error deleting document: ", e);
      }
    }

    async function approveTask(e, taskIndex) {
      e.preventDefault();

      const data = new FormData(e.target);
      const task = Object.fromEntries(data.entries());

      if (task.type == "multipleChoice" || task.type == "multipleAnswer") {
        task.solution = data.getAll("solution");
        task.options = shuffleArray(data.getAll("options"));
      }

      if (task.type == "fillTheBlanks") {
        if (task.solution.length) {
          task.solution = removeLeadingTrailingCommas(task.solution).split(",");
          task.solution = task.solution.map((str) => str.trim());
        }
        task.options = [createFillString(data.getAll("options")[0], task.solution)];
      }

      if (task.type == "trueFalse" || task.type == "textShort") {
        task.solution = [task.solution];
        task.options = data.getAll("options");
      }

      if (task.type == "sequence") {
        task.solution = data.getAll("solution");
        task.options = shuffleArray(data.getAll("solution"));
      }

      if (task.type == "reading") {
        const bullets = data.getAll("bullets");
        // remove empty bullets
        if (bullets) {
          task.reading = {
            bullets,
          };
          delete task.bullets;
        } else {
          task.reading = {
            text: document.getElementById(`task-reading-html-${taskIndex}`).firstChild.innerHTML,
          };
        }
      }

      // error handling for incomplete task
      if (task.type && task.type !== "reading" && !task.solution.length) {
        return toast.error("Please provide the correct solution");
      }

      try {
        const sourceRef = doc(db, "sources", reviewTasks.value[taskIndex].source.id);

        // prep task to be added to source document
        const sourceTask = { ...reviewTasks.value[taskIndex] };
        if (task.type == "reading") {
          sourceTask.reading = task.reading;
        }

        // remove unnecessary fields
        delete sourceTask.source;
        delete sourceTask.numTasks;
        delete sourceTask.id;
        // add task to source
        await updateDoc(sourceRef, {
          tasks: arrayUnion(sourceTask),
        });

        task.review = {
          approved: true,
          by: user.value.uid,
          at: Timestamp.now(),
        };

        // update task document
        await updateDoc(doc(db, "tasks", reviewTasks.value[taskIndex].id), task);

        const tasksLeftForUser = reviewTasks.value.filter((taskList) => taskList.user === reviewTasks.value[taskIndex].user);

        // if only 1 tasks left to review for this user send email notification.
        // can't send at 0 tasks left currently because don't have the relevant user id anymore.
        if (tasksLeftForUser.length == 1) {
          const taskUserData = await getUser(sourceTask.user);
          // all tasks for this user have been approved
          // TODO improve this by checking the current status of today's review (if notifications have already been sent)

          // send review notification (preference on mobile, email as fallback)
          if (taskUserData.channels?.whatsapp?.id || taskUserData.channels?.sms?.id) {
            const sender = taskUserData.channels.whatsapp.id ? "whatsapp:+18317039919" : "+18317039919";
            await addDoc(collection(db, "phone-notifications"), {
              from: sender,
              to: taskUserData.channels.whatsapp.id || taskUserData.channels.sms.id,
              body: `Hi ${taskUserData.firstName}, your content review for today is ready now.`,
            });
            toast.success(`Notifications sent to ${taskUserData.channels.whatsapp.id || taskUserData.channels.sms.id}`);
          } else {
            const firstName = taskUserData.firstName || "";
            const subject = firstName ? `${firstName}'s content review is ready now` : "Your review is ready now";
            await addDoc(collection(db, "email-notifications"), {
              to: taskUserData.email,
              template: {
                name: "review-notification-plain",
                data: {
                  firstName: firstName,
                  greeting: taskUserData.firstName ? `Hi ${taskUserData.firstName},` : "Hi,",
                  openingLine: "Your content review is ready now! Review the summaries and short quizzes and see how information is committed to your long term memory.",
                  cta: "Review Now",
                  subject,
                },
              },
            });

            toast.success(`Email sent to ${taskUserData.channels.email.id}`);
          }
        }

        return toast.success("Approved");
      } catch (error) {
        console.error(error);
        return toast.error(`Error ${error.message}`);
      }
    }

    return {
      user,
      reviewTasks,
      tasksLoaded,
      answerOptionsLength,
      deleteTask,
      approveTask,
      reviewerUserData,
      dateString,
    };
  },
};
</script>
